import React, { useEffect, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { registrarIngresoLibre } from '../services/ingresosService';
import { registrarComisionBancaria } from '../services/gastosService';
import { calcularComision, COMISION_POS_PCT } from '../utils/comisiones';
import { obtenerComisionPct } from '../services/negociosService';
import { obtenerClientes } from '../services/clientesService';
import NuevoClienteInline from './NuevoClienteInline';
import { obtenerCuentas, iconoTipo } from '../services/cuentasService';
import { CANALES, CANAL_DEFECTO } from '../utils/canales';
import { mensajeAmigable } from '../utils/errores';
import { numeroSeguro } from '../utils/validacion';
import { fechaLocal } from '../utils/fecha';
import CampoNumero from './CampoNumero';
import { useToast } from '../context/ToastContext';

const fmt = (n) => `${simboloMoneda()} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Venta libre / otro ingreso: dinero que entra SIN tocar inventario.
// Para vender algo que no está registrado, o para ingresos de otra
// fuente (consultorías, servicios). Va a "Otros ingresos" en reportes.
export default function IngresoLibreModal({ onCerrar, onGuardado }) {
  const { mostrarToast } = useToast();
  const [concepto, setConcepto] = useState('');
  const [precio, setPrecio] = useState('');
  const [costo, setCosto] = useState('');
  const [descuento, setDescuento] = useState('');
  const [envio, setEnvio] = useState('');
  const [cuentaId, setCuentaId] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [canal, setCanal] = useState(CANAL_DEFECTO);
  const [fecha, setFecha] = useState(fechaLocal());
  const [conComision, setConComision] = useState(false); // cobrado con POS/Link (−%)
  const [comisionPct, setComisionPct] = useState(COMISION_POS_PCT);
  const [cuentas, setCuentas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    obtenerCuentas().then((cs) => {
      setCuentas(cs);
      if (cs.length) setCuentaId(cs[0].id);
    }).catch(() => {});
    obtenerClientes().then(setClientes).catch(() => {});
    obtenerComisionPct().then(setComisionPct).catch(() => {});
  }, []);

  const precioNum = numeroSeguro(precio);
  const costoNum = numeroSeguro(costo);
  const descuentoNum = numeroSeguro(descuento);
  const envioNum = numeroSeguro(envio);
  const netoProducto = Math.max(0, precioNum - descuentoNum);
  const neto = netoProducto + envioNum; // total que entra a la cuenta
  const ganancia = netoProducto - costoNum; // envío no cuenta como ganancia
  const comisionMonto = conComision ? calcularComision(neto, comisionPct) : 0; // 4% sobre lo que entra
  const puedeGuardar = concepto.trim() !== '' && neto > 0 && descuentoNum <= precioNum && cuentaId && !guardando;

  const guardar = async () => {
    if (!puedeGuardar) return;
    setGuardando(true);
    try {
      await registrarIngresoLibre({ concepto, monto: neto, descuento: descuentoNum, envio: envioNum, costo: costoNum, cuentaId, clienteId: clienteId || null, fecha, canal });
      // Comisión bancaria automática si se cobró con POS / Link de pago.
      if (comisionMonto > 0 && cuentaId) {
        try {
          await registrarComisionBancaria({ monto: comisionMonto, cuentaId, fecha, detalle: `POS / Link · ${concepto}`.slice(0, 120) });
        } catch (err) {
          mostrarToast('El ingreso se registró, pero no se pudo registrar la comisión. Agrégala a mano en 💸 Gastos.', 'error', 7000);
        }
      }
      mostrarToast(`¡Ingreso registrado! ${fmt(neto)} 💰`, 'exito');
      onGuardado?.();
      onCerrar();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally {
      setGuardando(false);
    }
  };

  const input = { width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
  const label = { fontSize: '12px', color: '#6B7280', fontWeight: 600, display: 'block', marginBottom: '4px' };

  return (
    <div onClick={onCerrar}
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 50 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '440px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #F3F4F6', backgroundColor: '#ECFDF5', borderRadius: '16px 16px 0 0' }}>
          <h3 style={{ margin: 0, color: 'var(--k-verde)', fontSize: '18px' }}>💵 Venta libre / Otro ingreso</h3>
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6B7280' }}>
            No toca el inventario. El dinero entra a la cuenta que elijas.
          </p>
        </div>

        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '14px', padding: '10px 12px', backgroundColor: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '8px' }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#9A3412', lineHeight: 1.5 }}>
              💡 ¿Vendés un <strong>producto</strong> y quieres que el costo sume a su <strong>categoría</strong> (recuperación por categoría)? Cerrá esto y usa <strong>"➕ Crear producto nuevo"</strong> en Vender. La <strong>venta libre</strong> es para ingresos que <strong>no son producto</strong> (consultoría) o una venta rápida sin categoría.
            </p>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={label}>¿Qué vendiste o de qué es el ingreso? *</label>
            <input type="text" value={concepto} autoFocus disabled={guardando}
              onChange={(e) => setConcepto(e.target.value)} style={input}
              placeholder="Ej: Perfume sin registrar / Consultoría / Envío" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
            <div>
              <label style={label}>Precio de venta (L)</label>
              <CampoNumero valor={precio} onChange={setPrecio} deshabilitado={guardando}
                min={0} prefijo="L" placeholder="0.00" estilo={input} />
            </div>
            <div>
              <label style={label}>Costo (L) — opcional</label>
              <CampoNumero valor={costo} onChange={setCosto} deshabilitado={guardando}
                min={0} prefijo="L" placeholder="0.00" estilo={input} />
            </div>
            <div>
              <label style={label}>Descuento (L)</label>
              <CampoNumero valor={descuento} onChange={setDescuento} deshabilitado={guardando}
                min={0} max={precioNum || undefined} prefijo="L" placeholder="0.00"
                maxMensaje="El descuento no puede ser mayor al precio de venta"
                estilo={input} />
            </div>
            <div>
              <label style={label}>Envío (L)</label>
              <CampoNumero valor={envio} onChange={setEnvio} deshabilitado={guardando}
                min={0} prefijo="L" placeholder="0.00" estilo={input} />
            </div>
          </div>

          {costoNum > 0 && netoProducto > 0 && (
            <div style={{ marginBottom: '12px', padding: '8px 12px', backgroundColor: ganancia >= 0 ? '#F0FDF4' : '#FEF2F2', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: `1px solid ${ganancia >= 0 ? '#BBF7D0' : '#FECACA'}` }}>
              <span style={{ fontSize: '13px', color: '#374151', fontWeight: 600 }}>📈 Ganancia (sin envío)</span>
              <span style={{ fontSize: '16px', fontWeight: 'bold', color: ganancia >= 0 ? 'var(--k-verde)' : '#DC2626' }}>
                {fmt(ganancia)}{precioNum > 0 && costoNum > 0 ? `  ·  ${Math.round((ganancia / costoNum) * 100)}%` : ''}
              </span>
            </div>
          )}
          <div style={{ marginBottom: '12px', padding: '10px 12px', backgroundColor: '#ECFDF5', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#065F46', fontWeight: 600 }}>Total que entra</span>
              <span style={{ fontSize: '18px', color: 'var(--k-verde)', fontWeight: 'bold' }}>{fmt(neto)}</span>
            </div>
            {envioNum > 0 && (
              <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'var(--k-verde)' }}>
                Incluye {fmt(netoProducto)} del producto + {fmt(envioNum)} de envío 📦
              </p>
            )}
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={label}>¿A qué cuenta entra el dinero? *</label>
            <select value={cuentaId} disabled={guardando} onChange={(e) => setCuentaId(e.target.value)} style={input}>
              {cuentas.length === 0 && <option value="">Crea una cuenta primero</option>}
              {cuentas.map((c) => <option key={c.id} value={c.id}>{iconoTipo(c.tipo)} {c.nombre}</option>)}
            </select>
          </div>

          <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', cursor: guardando ? 'default' : 'pointer' }}>
            <input type="checkbox" checked={conComision} disabled={guardando}
              onChange={(e) => setConComision(e.target.checked)} style={{ marginTop: '2px', width: '16px', height: '16px', flexShrink: 0 }} />
            <span style={{ fontSize: '12px', color: '#1E40AF' }}>
              <strong>💳 Cobrado con POS o Link de pago</strong> <span style={{ color: '#1D4ED8' }}>(−{comisionPct}% comisión)</span>
              {conComision && comisionMonto > 0 ? (
                <span style={{ display: 'block', marginTop: '3px', color: '#1D4ED8', fontWeight: 700 }}>
                  Se rebajará {fmt(comisionMonto)} de tu cuenta como comisión bancaria.
                </span>
              ) : (
                <span style={{ display: 'block', marginTop: '3px', color: '#3B82F6' }}>
                  El banco te debita el {comisionPct}%. Se registra como gasto "Comisiones bancarias".
                </span>
              )}
            </span>
          </label>

          <div style={{ marginBottom: '12px' }}>
            <label style={label}>Canal de venta</label>
            <select value={canal} disabled={guardando} onChange={(e) => setCanal(e.target.value)} style={input}>
              {CANALES.map((c) => <option key={c.valor} value={c.valor}>{c.icono} {c.valor}</option>)}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '18px' }}>
            <div>
              <label style={label}>Cliente (opcional)</label>
              <select value={clienteId} disabled={guardando} onChange={(e) => setClienteId(e.target.value)} style={input}>
                <option value="">Sin cliente</option>
                {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
            <div>
              <label style={label}>Fecha</label>
              <input type="date" value={fecha} disabled={guardando}
                onChange={(e) => setFecha(e.target.value)} style={input} />
            </div>
          </div>

          <div style={{ marginTop: '-8px', marginBottom: '18px' }}>
            <NuevoClienteInline disabled={guardando} onCreado={(c) => {
              setClientes((prev) => [...prev, c].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')));
              setClienteId(c.id);
            }} />
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={onCerrar} disabled={guardando}
              style={{ flex: '0 0 auto', padding: '12px 18px', background: 'white', border: '1px solid #D1D5DB', borderRadius: '10px', cursor: 'pointer', color: '#6B7280', fontWeight: 600 }}>
              Cancelar
            </button>
            <button onClick={guardar} disabled={!puedeGuardar}
              style={{ flex: 1, padding: '12px', backgroundColor: puedeGuardar ? 'var(--k-verde)' : '#9CA3AF', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: puedeGuardar ? 'pointer' : 'not-allowed' }}>
              {guardando ? '⏳ Guardando...' : `💰 Registrar ${neto > 0 ? fmt(neto) : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
